/**
 * Plugin to activate stuff when objects come in view
 * Also for lazy images / lazy interchange
 * created by bogdan.b@codingheads.com
 * requires IntersectionObserver
 *
 * version 0.2
 */
class activateOnScroll {
  // store the observers into an array, so that we might reuse them
  static savedObservers = [];

  // the options with their defaults
  activatedClass = 'animation-started';
  activatedClassTargetSelector = false; // where to add the activatedClass
  initAttribute = 'activateOnScrollInit';
  options = {
    rootMargin: '300px 100px',
  };
  callback = false;
  removable = true;
  fadeIn = false;
  activateWithParent = true;

  // deep compare objects
  static deepEquals(x, y) {
    if (x === y) return true;
    // if both x and y are null or undefined and exactly the same

    if (!(x instanceof Object) || !(y instanceof Object)) return false;
    // if they are not strictly equal, they both need to be Objects

    if (x.constructor !== y.constructor) return false;
    // they must have the exact same prototype chain, the closest we can do is
    // test there constructor.

    for (let p in x) {
      if (!x.hasOwnProperty(p)) continue;
      // other properties were tested using x.constructor === y.constructor

      if (!y.hasOwnProperty(p)) return false;
      // allows to compare x[ p ] and y[ p ] when set to undefined

      if (x[p] === y[p]) continue;
      // if they have the same strict value or identity then they are equal

      if (typeof x[p] !== 'object') return false;
      // Numbers, Strings, Functions, Booleans must be strictly equal

      if (!this.deepEquals(x[p], y[p])) return false;
      // Objects and Arrays must be tested recursively
    }

    for (let p in y) {
      if (y.hasOwnProperty(p) && !x.hasOwnProperty(p)) return false;
      // allows x[ p ] to be set to undefined
    }
    return true;
  }

  static throttle = (func, limit = 100) => {
    let inThrottle;
    return function() {
      const args = arguments,
        context = this;
      if (!inThrottle) {
        func.apply(context, args);
        inThrottle = true;
        setTimeout(() => (inThrottle = false), limit);
      }
    };
  };

  // trigger a window resize - but throttle it
  static triggerResize = this.throttle(() => {
    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(() => {
        window.requestAnimationFrame(() => {
          window.dispatchEvent(new CustomEvent('resize'));
        });
      });
    } else {
      window.requestAnimationFrame(() => {
        window.dispatchEvent(new CustomEvent('resize'));
      });
    }
  }, 100);

  // finish activating the element - when the image is loaded
  static finishActivatingElement = (
    element,
    {
      activatedClass = '',
      isLazyBg = false,
      isLazyImg = false,
      interchangeSizes = false,
      interchange = false,
      intoViewTimestamp = false,
      fadeIn = false,
      inPicture = false,
    } = {}
  ) => {
    requestAnimationFrame(() => {
      if (activatedClass.length) {
        element.classList.add(activatedClass);
      }
      element.dataset.lazyLoaded = true;
    });
    if (fadeIn) {
      // wait for a minimum interval before fading in
      const minInterval = 200,
        time = new Date(),
        interval = time.getTime() - intoViewTimestamp;
      if (element.tagName == 'IMG' || interval > minInterval) {
        requestAnimationFrame(() => {
          element.style.opacity = 1;
        });
      } else {
        setTimeout(() => {
          requestAnimationFrame(() => {
            element.style.opacity = 1;
          });
        }, minInterval - interval);
      }
    }
    if (isLazyImg && !interchangeSizes && !inPicture) {
      // trigger a window resize - but throttle it
      this.triggerResize();
    }
    if (
      interchangeSizes &&
      (isLazyImg || (interchange && 'foundationLoaded' in element.dataset))
    ) {
      requestAnimationFrame(() => {
        if (isLazyImg) element.dataset.imgLoaded = true;
        element.removeAttribute('width');
        element.removeAttribute('height');
        element.style.height = '';
        element.style.width = '';
      });
    }
  };

  // activate the element - change src / interchange
  static activateElement = (
    element,
    { activatedClass = '', fadeIn = false, inPicture = false } = {}
  ) => {
    const time = new Date();
    const intoViewTimestamp = time.getTime();
    const dataset = element.dataset,
      interchange = dataset.lazyInterchange,
      isLazyBg = 'lazyBg' in dataset,
      isLazyImg = 'lazyImg' in dataset || 'srcset' in dataset,
      interchangeSizes = dataset.interchangeSizes;
    const finish = () => {
      this.finishActivatingElement(element, {
        activatedClass,
        isLazyBg,
        isLazyImg,
        interchangeSizes,
        interchange,
        intoViewTimestamp,
        fadeIn,
        inPicture,
      });
    };

    if (interchange || isLazyBg || isLazyImg) {
      if (!inPicture) {
        imagesLoaded(element, { background: true }, finish);
      } else {
        if (element.tagName == 'IMG') {
          element.onload = function() {
            finish();
            element.onload = null;
          };
        } else {
          finish();
        }
      }
      if (fadeIn) {
        requestAnimationFrame(() => {
          element.style.opacity = 0;
          element.style.transition = 'opacity 0.3s';
        });
      }
      // handle lazy interchange cases
      if (interchange) {
        element.dataset.interchange = interchange;
        element.removeAttribute('data-lazy-interchange');
        element.dataset.foundationLoaded = true;
        if ('jQuery' in window && 'foundation' in jQuery.fn) {
          setTimeout(() => {
            jQuery(element).foundation();
          });
        }
      }
      if (isLazyBg || isLazyImg) {
        requestAnimationFrame(() => {
          const url = dataset.src;
          if (isLazyBg) {
            // background image
            element.style.backgroundImage = `url(${url})`;
            element.removeAttribute('data-lazy-bg');
          } else {
            // img element
            if (url) {
              element.setAttribute('src', url);
              element.removeAttribute('data-src');
            }
            element.removeAttribute('data-lazy-img');
            const srcset = dataset.srcset,
              sizes = dataset.sizes;
            if (srcset) {
              element.setAttribute('srcset', srcset);
              element.removeAttribute('data-srcset');
            }
            if (sizes) {
              element.setAttribute('sizes', sizes);
              element.removeAttribute('data-sizes');
            }
          }
        });
      }
    }
  };

  // handle the event when the element gets into the viewport
  trigger() {
    const targetEl = this.activatedClassTargetSelector
      ? this.element.querySelector(this.activatedClassTargetSelector)
      : false;
    const target = targetEl ? targetEl : this.element;

    // custom activated class
    const elementactivatedClass = target.dataset.activated;
    if (elementactivatedClass) {
      this.activatedClass = elementactivatedClass;
    }

    // fade in?
    if ('fadeIn' in this.element.dataset) {
      this.fadeIn = true;
    }

    // add animation class
    this.lazyAnimClass = target.dataset.lazyAnimation;
    if (
      this.activatedClass &&
      this.activatedClass.length &&
      !this.lazyAnimClass
    ) {
      requestAnimationFrame(() => {
        target.classList.add(this.activatedClass);
      });
    }

    // trigger the callback
    if (this.callback) {
      setTimeout(() =>
        this.callback(this.element, {
          activatedClass: this.activatedClass,
          callback: this.callback,
          fadeIn: this.fadeIn,
          lazyAnimClass: this.lazyAnimClass,
          instance: this,
        })
      );
    }

    // if this is a lazy-image animation, do things differently
    if (this.lazyAnimClass) {
      requestAnimationFrame(() => {
        this.element.classList.add('opacity-0');
        requestAnimationFrame(() => {
          this.element.classList.add(this.lazyAnimClass);
          this.element.classList.remove('opacity-0'); // prevent fading out first
        });
      });
    }

    // trigger loading for picture source tags
    const parent = this.element.parentElement;
    const inPicture =
      this.element.tagName == 'IMG' && parent.tagName == 'PICTURE';
    if (inPicture) {
      parent.querySelectorAll('source').forEach(element =>
        this.constructor.activateElement(element, {
          inPicture: true,
        })
      );
    }

    // trigger loading on the tag
    this.constructor.activateElement(this.element, {
      activatedClass: this.activatedClass,
      fadeIn: this.fadeIn,
      inPicture,
    });

    // trigger an event...
    this.element.dispatchEvent(
      new CustomEvent('activatedOnScroll', {
        bubbles: true,
        detail: {
          instance: this,
        },
      })
    );
  }

  init() {
    // don't duplicate activations
    if (this.initAttribute in this.element.dataset) return;

    // signal that this is initialized
    this.element.dataset[this.initAttribute] = true;

    // allow external trigger
    this.element.addEventListener('activateOnScroll', e => {
      if (!this.activateWithParent || e.target == this.element) {
        this.trigger();
      }
    });

    // set the width and height of the image even if it's not loaded yet, so no moving around will happen on load
    const interchangeSizesJSON = this.element.dataset.interchangeSizes;
    if (interchangeSizesJSON && typeof Foundation.MediaQuery != 'undefined') {
      let interchangeSizes;
      try {
        interchangeSizes = JSON.parse(interchangeSizesJSON);
      } catch (error) {
        interchangeSizes = [];
      }
      if (interchangeSizes) {
        const addImageSizes = () => {
          window.requestAnimationFrame(() => {
            let currentSize = Foundation.MediaQuery.current;
            // if foundation is already loaded, don't do this any more
            if ('foundationLoaded' in this.element.dataset) {
              ['changed.zf.mediaquery'].forEach(eventType => {
                window.removeEventListener(eventType, addImageSizes);
              });
              return;
            }
            if (!(currentSize in interchangeSizes)) {
              // search a valid image size
              const availableSizes = Object.getOwnPropertyNames(
                interchangeSizes
              ).reverse();
              for (let i = 0, max = availableSizes.length; i < max; i++) {
                if (Foundation.MediaQuery.atLeast(availableSizes[i])) {
                  currentSize = availableSizes[i];
                  break;
                }
              }
            }
            if (currentSize in interchangeSizes) {
              const width = this.element.clientWidth,
                minWidth =
                  width > 0
                    ? Math.min(width, interchangeSizes[currentSize].w)
                    : interchangeSizes[currentSize].w,
                height =
                  (minWidth / interchangeSizes[currentSize].w) *
                  interchangeSizes[currentSize].h;
              this.element.setAttribute(
                'width',
                interchangeSizes[currentSize].w
              );
              this.element.setAttribute(
                'height',
                interchangeSizes[currentSize].h
              );
              this.element.style.width = minWidth + 'px';
              this.element.style.height = height + 'px';
            }
          });
        };
        addImageSizes();
        ['changed.zf.mediaquery'].forEach(eventType => {
          window.addEventListener(eventType, addImageSizes);
        });
      }
    }
  }

  constructor(
    element,
    {
      activatedClass = 'animation-started',
      activatedClassTargetSelector = false,
      initAttribute = 'activateOnScrollInit',
      options = {
        rootMargin: '100px 0px', // trigger the animation before the objects are in sight
      },
      callback = false,
      removable = true,
      fadeIn = false,
      activateWithParent = true,
    } = {}
  ) {
    // store the settings
    const args = {
      activatedClass,
      activatedClassTargetSelector,
      initAttribute,
      options,
      callback,
      removable,
      fadeIn,
      activateWithParent,
    };
    this.element = element;
    this.element.activateOnScrollInstance = this;
    Object.keys(args).forEach(setting => (this[setting] = args[setting]));

    // search for an existing observer
    let observer = false;
    if (this.constructor.savedObservers.length) {
      const match = this.constructor.savedObservers.filter(savedObserver =>
        this.constructor.deepEquals(savedObserver.args, args)
      );
      if (match && match.length) {
        observer = match[0].observer;
      }
    }

    // no previous matching observer found. Create a new one
    if (!observer) {
      observer = new IntersectionObserver(function(entries) {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            // allow cancelling the event
            if (
              !entry.target.dispatchEvent(
                new CustomEvent('willActivateOnScroll', {
                  bubbles: true,
                  detail: {
                    entry,
                    observer,
                    ...args,
                    instance: this,
                  },
                })
              )
            )
              return;

            // don't reobserve
            if (removable) observer.unobserve(entry.target);

            entry.target.dispatchEvent(
              new CustomEvent('activateOnScroll', {
                bubbles: true,
                detail: {
                  entry,
                  observer,
                  ...args,
                  instance: this,
                },
              })
            );
          }
        });
      }, options);
      // save the observer
      this.constructor.savedObservers.push({
        observer,
        args,
      });
    }
    this.observer = observer;

    // initialize the observer on this element
    this.observer.observe(this.element);

    // unobserve when in view
    this.element.addEventListener('activatedOnScroll', e => {
      if (e.target == this && this.removable) {
        this.observer.unobserve(this.element);
        this.element.activateOnScrollInstance = null;
      }
    });

    // initialize the event handlers
    this.init();
  }
}

window.activateOnScroll = activateOnScroll;

/* register a jQuery plugin, if jquery is on the page */
if ('jQuery' in window) {
  jQuery(function($) {
    // activate on scroll, using the intersection observer
    $.fn.activateOnScroll = function(options) {
      this.each((i, element) => new activateOnScroll(element, options));
      return this;
    };
  });
}
